import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';

const ActionButtons = ({ onSaveTrip, isAuthenticated, isSaving }) => {
  const navigate = useNavigate();

  const handleBackToEdit = () => {
    navigate('/trip-review-edit');
  };

  const handleSaveTrip = () => {
    if (isAuthenticated) {
      onSaveTrip();
    } else {
      // Redirect to registration with trip data
      navigate('/user-registration', { 
        state: { 
          returnTo: '/pdf-generation-download',
          message: 'Create an account to save your trip and access it anytime!'
        }
      });
    }
  };

  const handleGoToDashboard = () => {
    if (isAuthenticated) {
      navigate('/user-dashboard');
    } else {
      navigate('/user-login');
    }
  };

  const handlePlanNewTrip = () => {
    navigate('/ai-trip-planning-interface');
  };

  return (
    <div className="bg-card border border-border rounded-organic shadow-organic">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-heading font-heading-bold text-card-foreground">Next Steps</h3>
        <p className="text-sm text-muted-foreground mt-1">
          What would you like to do next?
        </p>
      </div>

      <div className="p-6 space-y-4">
        {/* Primary Actions */}
        <div className="space-y-3">
          <Button
            variant="default"
            fullWidth
            onClick={handleSaveTrip}
            loading={isSaving}
            iconName={isAuthenticated ? "Save" : "UserPlus"}
            iconPosition="left"
          >
            {isAuthenticated ? "Save Trip to Dashboard" : "Create Account & Save Trip"}
          </Button>

          <Button
            variant="outline"
            fullWidth
            onClick={handleBackToEdit}
            iconName="Edit"
            iconPosition="left"
          >
            Back to Edit Trip
          </Button>
        </div>

        {/* Secondary Actions */}
        <div className="border-t border-border pt-4 space-y-3">
          {isAuthenticated && (
            <Button
              variant="ghost"
              fullWidth
              onClick={handleGoToDashboard}
              iconName="LayoutDashboard"
              iconPosition="left"
            >
              Go to My Dashboard
            </Button>
          )}

          <Button
            variant="ghost"
            fullWidth
            onClick={handlePlanNewTrip}
            iconName="Plus"
            iconPosition="left"
          >
            Plan Another Trip
          </Button>
        </div>

        {/* Account Prompt for Non-Authenticated Users */}
        {!isAuthenticated && (
          <div className="bg-primary/5 border border-primary/20 rounded-organic-sm p-4 mt-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary/10 rounded-organic flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-body font-body-semibold text-primary text-sm">
                  Save Your Trip
                </h4>
                <p className="text-sm text-primary/80 mt-1">
                  Create a free account to save this trip, access it from any device, and build your travel history.
                </p>
                <div className="flex items-center space-x-3 mt-3">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => navigate('/user-registration')}
                  >
                    Sign Up Free
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/user-login')}
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Message for Authenticated Users */}
        {isAuthenticated && (
          <div className="bg-success/5 border border-success/20 rounded-organic-sm p-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-success/10 rounded-organic flex items-center justify-center">
                <svg className="w-4 h-4 text-success" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-body font-body-medium text-success text-sm">
                  Ready to Save
                </p>
                <p className="text-sm text-success/80">
                  Your trip will be saved to your dashboard for easy access.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionButtons;